export interface InvoiceCalculationInputItem {
  quantity: number;
  unit_price: number;
  tax_rate: number;
  discount_amount?: number;
  product_id?: string | null;
  product_name?: string;
  description?: string | null;
  unit_name?: string;
  is_gross?: boolean;
}

export interface NormalizedInvoiceItem extends InvoiceCalculationInputItem {
  quantity: number;
  unit_price: number;
  tax_rate: number;
  discount_amount: number;
  amount: number;
  gross_amount: number;
  tax_amount: number;
  _gross_before_discount: number;
}

interface CalculateInvoiceSummaryParams {
  items: InvoiceCalculationInputItem[];
  voucherAmount?: number;
  invoiceDiscount?: number;
  paymentMethodFee?: number;
  paymentMethodFeeTaxRate?: number;
}

interface TaxGroupSummary {
  rate: number;
  baseBeforeGlobalDiscount: number;
  globalDiscountAllocated: number;
  baseAfterGlobalDiscount: number;
  taxAmount: number;
}

export interface InvoiceSummaryCalculation {
  normalizedItems: NormalizedInvoiceItem[];
  subtotalBeforeDiscount: number;
  itemDiscountTotal: number;
  subtotal: number;
  voucherAmount: number;
  maxDiscountableAmount: number;
  normalizedInvoiceDiscount: number;
  paymentMethodFee: number;
  taxAmounts: Record<number, number>;
  taxGroups: TaxGroupSummary[];
  totalNet: number;
  totalTax: number;
  totalGross: number;
}

const toCents = (value: number): number => Math.round((Number.isFinite(value) ? value : 0) * 100);
const fromCents = (value: number): number => value / 100;
const clamp = (value: number, min: number, max: number): number => Math.min(Math.max(value, min), max);

export const normalizeInvoiceItems = (items: InvoiceCalculationInputItem[]): NormalizedInvoiceItem[] => {
  return items.map((item) => {
    const quantity = Math.max(0, Number(item.quantity) || 0);
    const taxRate = Number(item.tax_rate) || 0;
    const unitPriceRaw = Math.max(0, Number(item.unit_price) || 0);
    const vatFactor = 1 + taxRate / 100;
    const unitPriceCents = toCents(item.is_gross ? unitPriceRaw / vatFactor : unitPriceRaw);
    const lineBeforeDiscountCents = Math.round(unitPriceCents * quantity);
    // Bei Brutto-Preiseingabe wird der Bruttowert in Cent separat gehalten,
    // damit der im Formular eingegebene Gesamtbetrag (z. B. 400,00 €) nicht auf 399,99 € abrundet.
    const grossBeforeDiscountCents = item.is_gross
      ? Math.round(toCents(unitPriceRaw) * quantity)
      : Math.round(lineBeforeDiscountCents * vatFactor);
    const discountCents = clamp(toCents(Math.max(0, Number(item.discount_amount) || 0)), 0, lineBeforeDiscountCents);

    const netAfterDiscountCents = lineBeforeDiscountCents - discountCents;
    const grossAfterDiscountCents = item.is_gross
      ? Math.max(0, grossBeforeDiscountCents - Math.round(discountCents * vatFactor))
      : Math.round(netAfterDiscountCents * vatFactor);
    const taxCents = grossAfterDiscountCents - netAfterDiscountCents;

    return {
      ...item,
      quantity,
      tax_rate: taxRate,
      unit_price: fromCents(unitPriceCents),
      discount_amount: fromCents(discountCents),
      amount: fromCents(netAfterDiscountCents),
      gross_amount: fromCents(grossAfterDiscountCents),
      tax_amount: fromCents(taxCents),
      _gross_before_discount: fromCents(grossBeforeDiscountCents),
    };
  });
};

export const calculateInvoiceSummary = ({
  items,
  voucherAmount = 0,
  invoiceDiscount = 0,
  paymentMethodFee = 0,
  paymentMethodFeeTaxRate = 19,
}: CalculateInvoiceSummaryParams): InvoiceSummaryCalculation => {
  const normalizedItems = normalizeInvoiceItems(items);

  const subtotalBeforeDiscountCents = normalizedItems.reduce(
    (sum, item) => sum + Math.round(item.quantity * toCents(item.unit_price)),
    0,
  );
  const itemDiscountTotalCents = normalizedItems.reduce((sum, item) => sum + toCents(item.discount_amount), 0);
  const subtotalCents = Math.max(0, subtotalBeforeDiscountCents - itemDiscountTotalCents);

  const normalizedVoucherCents = clamp(toCents(voucherAmount), 0, subtotalCents);
  const maxDiscountableAmountCents = Math.max(0, subtotalCents - normalizedVoucherCents);
  const normalizedInvoiceDiscountCents = clamp(toCents(invoiceDiscount), 0, maxDiscountableAmountCents);
  const globalDiscountCents = normalizedVoucherCents + normalizedInvoiceDiscountCents;
  const paymentFeeCents = Math.max(0, toCents(paymentMethodFee));

  // Ohne globalen Netto-Rabatt können die Steuerbeträge direkt aus den positionsgenauen Cent-Werten
  // übernommen werden. Das verhindert Rundungsdrift bei Brutto-Eingaben (z. B. 400,00 € -> 399,99 €).
  if (globalDiscountCents === 0) {
    const taxGroupBaseCents = normalizedItems.reduce((acc, item) => {
      const rate = item.tax_rate;
      const amountCents = toCents(item.amount);
      acc[rate] = (acc[rate] ?? 0) + amountCents;
      return acc;
    }, {} as Record<number, number>);

    const taxAmountsCents = normalizedItems.reduce((acc, item) => {
      const rate = item.tax_rate;
      acc[rate] = (acc[rate] ?? 0) + toCents(item.tax_amount);
      return acc;
    }, {} as Record<number, number>);

    if (paymentFeeCents > 0) {
      const feeTaxRate = Number(paymentMethodFeeTaxRate) || 0;
      const feeTaxCents = Math.round((paymentFeeCents * feeTaxRate) / 100);
      taxAmountsCents[feeTaxRate] = (taxAmountsCents[feeTaxRate] ?? 0) + feeTaxCents;
      taxGroupBaseCents[feeTaxRate] = (taxGroupBaseCents[feeTaxRate] ?? 0) + paymentFeeCents;
    }

    const taxGroups = Object.keys(taxGroupBaseCents)
      .map((rateValue) => Number(rateValue))
      .sort((a, b) => a - b)
      .map((rate) => ({
        rate,
        baseBeforeGlobalDiscount: fromCents(taxGroupBaseCents[rate] ?? 0),
        globalDiscountAllocated: 0,
        baseAfterGlobalDiscount: fromCents(taxGroupBaseCents[rate] ?? 0),
        taxAmount: fromCents(taxAmountsCents[rate] ?? 0),
      }));

    const netAfterDiscountsCents = subtotalCents;
    const totalNetCents = netAfterDiscountsCents + paymentFeeCents;
    const totalTaxCents = Object.values(taxAmountsCents).reduce((sum, amount) => sum + amount, 0);
    const totalGrossCents = totalNetCents + totalTaxCents;

    const taxAmounts = Object.entries(taxAmountsCents).reduce((acc, [rate, amount]) => {
      acc[Number(rate)] = fromCents(amount);
      return acc;
    }, {} as Record<number, number>);

    return {
      normalizedItems,
      subtotalBeforeDiscount: fromCents(subtotalBeforeDiscountCents),
      itemDiscountTotal: fromCents(itemDiscountTotalCents),
      subtotal: fromCents(subtotalCents),
      voucherAmount: fromCents(normalizedVoucherCents),
      maxDiscountableAmount: fromCents(maxDiscountableAmountCents),
      normalizedInvoiceDiscount: fromCents(normalizedInvoiceDiscountCents),
      paymentMethodFee: fromCents(paymentFeeCents),
      taxAmounts,
      taxGroups,
      totalNet: fromCents(totalNetCents),
      totalTax: fromCents(totalTaxCents),
      totalGross: fromCents(totalGrossCents),
    };
  }

  const taxGroupBaseCents = normalizedItems.reduce((acc, item) => {
    const rate = item.tax_rate;
    const amountCents = toCents(item.amount);
    acc[rate] = (acc[rate] ?? 0) + amountCents;
    return acc;
  }, {} as Record<number, number>);

  const taxRates = Object.keys(taxGroupBaseCents)
    .map((rate) => Number(rate))
    .sort((a, b) => a - b);

  const allocationDetails = taxRates.map((rate) => {
    const baseCents = taxGroupBaseCents[rate] ?? 0;
    const rawAllocation = subtotalCents > 0 ? (globalDiscountCents * baseCents) / subtotalCents : 0;
    const allocatedFloor = Math.floor(rawAllocation);
    return {
      rate,
      baseCents,
      allocatedFloor,
      remainder: rawAllocation - allocatedFloor,
    };
  });

  let allocatedCents = allocationDetails.reduce((sum, entry) => sum + entry.allocatedFloor, 0);
  let centsToDistribute = Math.max(0, globalDiscountCents - allocatedCents);

  allocationDetails
    .sort((a, b) => {
      if (b.remainder !== a.remainder) return b.remainder - a.remainder;
      if (b.baseCents !== a.baseCents) return b.baseCents - a.baseCents;
      return a.rate - b.rate;
    })
    .forEach((entry) => {
      if (centsToDistribute <= 0) return;
      entry.allocatedFloor += 1;
      centsToDistribute -= 1;
    });

  const taxAmountsCents: Record<number, number> = {};
  const taxGroups = allocationDetails
    .sort((a, b) => a.rate - b.rate)
    .map((entry) => {
      const baseAfterDiscountCents = Math.max(0, entry.baseCents - entry.allocatedFloor);
      const taxCents = Math.round((baseAfterDiscountCents * entry.rate) / 100);
      taxAmountsCents[entry.rate] = taxCents;

      return {
        rate: entry.rate,
        baseBeforeGlobalDiscount: fromCents(entry.baseCents),
        globalDiscountAllocated: fromCents(entry.allocatedFloor),
        baseAfterGlobalDiscount: fromCents(baseAfterDiscountCents),
        taxAmount: fromCents(taxCents),
      };
    });

  if (paymentFeeCents > 0) {
    const feeTaxRate = Number(paymentMethodFeeTaxRate) || 0;
    const feeTaxCents = Math.round((paymentFeeCents * feeTaxRate) / 100);
    taxAmountsCents[feeTaxRate] = (taxAmountsCents[feeTaxRate] ?? 0) + feeTaxCents;
  }

  const netAfterDiscountsCents = Math.max(0, subtotalCents - globalDiscountCents);
  const totalNetCents = netAfterDiscountsCents + paymentFeeCents;
  const totalTaxCents = Object.values(taxAmountsCents).reduce((sum, amount) => sum + amount, 0);
  const totalGrossCents = totalNetCents + totalTaxCents;

  const taxAmounts = Object.entries(taxAmountsCents).reduce((acc, [rate, amount]) => {
    acc[Number(rate)] = fromCents(amount);
    return acc;
  }, {} as Record<number, number>);

  return {
    normalizedItems,
    subtotalBeforeDiscount: fromCents(subtotalBeforeDiscountCents),
    itemDiscountTotal: fromCents(itemDiscountTotalCents),
    subtotal: fromCents(subtotalCents),
    voucherAmount: fromCents(normalizedVoucherCents),
    maxDiscountableAmount: fromCents(maxDiscountableAmountCents),
    normalizedInvoiceDiscount: fromCents(normalizedInvoiceDiscountCents),
    paymentMethodFee: fromCents(paymentFeeCents),
    taxAmounts,
    taxGroups,
    totalNet: fromCents(totalNetCents),
    totalTax: fromCents(totalTaxCents),
    totalGross: fromCents(totalGrossCents),
  };
};
